import { isDefined } from '@sgme/fp';
import { LimitSecondarySummaryModal } from 'components/common-form/sidePanel/docToolbar/summary/LimitSecondarySummaryModal';
import { LiveSecondarySummaryModal } from 'components/common-form/sidePanel/docToolbar/summary/LiveSecondarySummaryModal';
import { SummaryMailSenderLimitSecondary } from 'components/common-form/sidePanel/docToolbar/summary/mailSender/MailSenderLimitSecondary';
import { SummaryMailSenderLiveSecondary } from 'components/common-form/sidePanel/docToolbar/summary/mailSender/MailSenderLiveSecondary';
import { SummaryMailSenderOrderSecondary } from 'components/common-form/sidePanel/docToolbar/summary/mailSender/MailSenderOrderSecondary';
import type { CpSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/cpSummaryMailRequest';
import type { LimitSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/limitSummaryMailRequest';
import type { LiveSummaryMailRequest } from 'components/common-form/sidePanel/docToolbar/summary/models/liveSummaryMailRequest';
import { OrderSecondarySummaryModal } from 'components/common-form/sidePanel/docToolbar/summary/OrderSecondarySummaryModal';
import { RawShareButton } from 'components/common-form/sidePanel/docToolbar/summary/ShareButton';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import type { ReactNode } from 'react';
import { FC, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { ShareButtonColor } from 'state/quotes/utils';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

interface ShareButtonSecondaryProps {
  tabId: string;
  mustForceAdvancedMode: boolean;
  workspaceType: QuoteWorkspaceType;
  color: ShareButtonColor;
  isDisabled?: boolean;

  summary?: string;
  type: 'live' | 'limit' | 'cp';
}
const id = 'shareButtonSecondary';

export type SendMailTypeProps = {
  sendMailType: 'live' | 'limit' | 'cp';
};

export type SecondarySummaryMailRequest = LiveSummaryMailRequest &
  CpSummaryMailRequest &
  LimitSummaryMailRequest & { summary?: string };

interface SummaryFieldRowSecondaryProps {
  labelKey?: IntlKey;
  children: ReactNode;
}

interface SecondarySummaryBlockProps {
  title: string;
  children: ReactNode;
}

export function SummaryFieldRowSecondary({ labelKey, children }: SummaryFieldRowSecondaryProps) {
  return (
    <div className="row">
      <div className="col-5">
        <p className="text-large fw-light text-secondary m-0 ms-3 ps-2">
          {isDefined(labelKey) ? <FormattedMessage id={labelKey} /> : ' '}
        </p>
      </div>
      <div className="col-7">{children}</div>
    </div>
  );
}

export function SecondarySummaryBlock({ title, children }: SecondarySummaryBlockProps) {
  return (
    <div data-e2e={id} id={id}>
      <table className="table table-sm mb-3" data-e2e={`product-summary-block-${id}`}>
        <thead>
          <tr>
            <th className="border-top-0 ps-0 text-start" col-span="2">
              <span className="badge badge-flat-primary badge-xl rounded-pill">
                <h5 className="mb-0">{title}</h5>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export const ShareButtonSecondary: FC<ShareButtonSecondaryProps> = ({ tabId, isDisabled, color, type, summary }) => {
  //Live Trade
  const [isOpened, setIsOpened] = useState(false);

  return (
    <>
      <RawShareButton
        tabId={tabId}
        color={color}
        isDisabled={isDisabled ?? isOpened}
        onShare={() => {
          logAnalytics.trackEvent('Secondary market', 'Share product summary');
          setIsOpened(!isOpened);
        }}
      />
      <Modal backdrop="static" centered fade show={isOpened} size="xl" onHide={() => setIsOpened(false)}>
        <ModalHeader onToggle={() => setIsOpened(!isOpened)} closeButton>
          <ModalTitle as="h4">
            {type === 'live'
              ? 'Live Secondary Summary'
              : type === 'limit'
                ? 'Limit Secondary Summary'
                : 'CP Secondary Summary'}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {type === 'live' ? (
            <LiveSecondarySummaryModal tabId={tabId}></LiveSecondarySummaryModal>
          ) : type === 'limit' ? (
            <LimitSecondarySummaryModal tabId={tabId} summary={summary}></LimitSecondarySummaryModal>
          ) : (
            <OrderSecondarySummaryModal tabId={tabId} summary={summary}></OrderSecondarySummaryModal>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-xl btn-default"
            onClick={() => setIsOpened(!isOpened)}
            data-e2e="close-product-summary"
          >
            <FormattedMessage id="close" />
          </button>
          {type === 'live' ? (
            <SummaryMailSenderLiveSecondary tabId={tabId}></SummaryMailSenderLiveSecondary>
          ) : type === 'limit' ? (
            <SummaryMailSenderLimitSecondary tabId={tabId} summary={summary}></SummaryMailSenderLimitSecondary>
          ) : (
            <SummaryMailSenderOrderSecondary tabId={tabId} summary={summary}></SummaryMailSenderOrderSecondary>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

ShareButtonSecondary.displayName = 'ShareButtonSecondary';
